export interface UserLocaleInfo {
  /** BCP 47 언어 태그 (예: "ko-KR", "en-US") */
  language: string;
  /** IANA 타임존 (예: "Asia/Seoul", "America/New_York") */
  timezone: string;
  /** ISO 3166-1 alpha-2 국가 코드 (예: "KR", "US") — 추론 실패 시 null */
  country_code: string | null;
  /** GPS 좌표 — 사용자가 권한 허용한 경우에만 존재 */
  coordinates?: { latitude: number; longitude: number };
}

const LOCALE_LANG_TO_COUNTRY: Record<string, string> = {
  ko: 'KR',
  ja: 'JP',
  zh: 'CN',
  'zh-TW': 'TW',
  'zh-HK': 'HK',
  'en-US': 'US',
  'en-GB': 'GB',
  'en-AU': 'AU',
  'en-CA': 'CA',
  de: 'DE',
  fr: 'FR',
  es: 'ES',
  it: 'IT',
  pt: 'PT',
  'pt-BR': 'BR',
  nl: 'NL',
  ru: 'RU',
  vi: 'VN',
  th: 'TH',
  id: 'ID',
  ms: 'MY',
  hi: 'IN',
  ar: 'SA',
  tr: 'TR',
  pl: 'PL',
  sv: 'SE',
  da: 'DK',
  fi: 'FI',
  nb: 'NO',
  nn: 'NO',
  uk: 'UA',
  cs: 'CZ',
  el: 'GR',
  ro: 'RO',
  hu: 'HU',
};

const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'Asia/Seoul': 'KR',
  'Asia/Tokyo': 'JP',
  'Asia/Taipei': 'TW',
  'Asia/Hong_Kong': 'HK',
  'Asia/Singapore': 'SG',
  'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID',
  'Asia/Manila': 'PH',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'Asia/Kolkata': 'IN',
  'Asia/Kathmandu': 'NP',
  'Asia/Colombo': 'LK',
  'Asia/Yerevan': 'AM',
  'Asia/Tbilisi': 'GE',
  'Asia/Baku': 'AZ',
  'Asia/Jerusalem': 'IL',
  'Asia/Tehran': 'IR',
  'Asia/Baghdad': 'IQ',
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'Europe/Copenhagen': 'DK',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO',
  'Europe/Athens': 'GR',
  'Europe/Lisbon': 'PT',
  'Europe/Istanbul': 'TR',
  'Europe/Kyiv': 'UA',
  'Europe/Moscow': 'RU',
  'Pacific/Auckland': 'NZ',
  'Australia/Sydney': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Africa/Johannesburg': 'ZA',
  'Africa/Cairo': 'EG',
  'Africa/Nairobi': 'KE',
  'America/Sao_Paulo': 'BR',
  'America/Mexico_City': 'MX',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Santiago': 'CL',
  'America/Buenos_Aires': 'AR',
};

function inferCountryCode(lang: string, timezone?: string): string | null {
  if (LOCALE_LANG_TO_COUNTRY[lang]) return LOCALE_LANG_TO_COUNTRY[lang];

  const parts = lang.split('-');
  if (parts.length >= 2) {
    const region = parts[parts.length - 1].toUpperCase();
    if (/^[A-Z]{2}$/.test(region)) return region;
  }

  if (LOCALE_LANG_TO_COUNTRY[parts[0]]) return LOCALE_LANG_TO_COUNTRY[parts[0]];

  if (timezone && TIMEZONE_TO_COUNTRY[timezone]) return TIMEZONE_TO_COUNTRY[timezone];

  return null;
}

let cachedLocale: UserLocaleInfo | null = null;

/**
 * 브라우저에서 즉시 수집 가능한 로케일 정보를 반환한다.
 * Geolocation 좌표는 비동기로 resolve되면 캐시에 병합된다.
 */
export function getUserLocale(): UserLocaleInfo {
  if (cachedLocale) return cachedLocale;

  const language = navigator.language || 'en';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const country_code = inferCountryCode(language, timezone);

  cachedLocale = { language, timezone, country_code };

  requestGeolocation();

  return cachedLocale;
}

function requestGeolocation(): void {
  if (!navigator.geolocation) return;

  navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
    if (result.state === 'granted') {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cachedLocale) {
            cachedLocale.coordinates = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            };
          }
        },
        () => {},
        { maximumAge: 300_000, timeout: 5_000 },
      );
    }
  });
}
